import type {InfiniteData, QueryClient} from "@tanstack/react-query";

import type {GetListResponse} from "@/src/modules/core/types";
import {NOTIFICATION_FEED_QUERY_KEY} from "@/src/modules/notification-feed/hooks/use-notification-feed";

import {DIRECT_MESSAGE_THREADS_QUERY_KEY} from "../hooks/use-direct-message-threads";
import {INFINITE_THREADS_QUERY_KEY} from "../hooks/use-infinite-direct-message-threads";
import {DIRECT_MESSAGE_THREAD_QUERY_KEY} from "../hooks/use-direct-message-thread";
import type {DirectMessagePost, DirectMessageThread, DirectMessageThreadDetail} from "../types";

import type {DirectMessageListOptions} from "../hooks/use-direct-message-threads";

function patchThreadAsReadInList(
  thread: DirectMessageThread,
  threadId: number,
): DirectMessageThread {
  if (thread.direct_message_thread_id !== threadId) {
    return thread;
  }
  return {
    ...thread,
    has_unread_manager_posts: false,
    has_unread_posts: false,
  };
}

/** Optimistic list update after GET /threads/{id} read-ack so inbox rows match the cleared badge. */
export function markThreadAsReadInListCaches(queryClient: QueryClient, threadId: number) {
  queryClient.setQueriesData<GetListResponse<DirectMessageThread>>(
    {queryKey: [DIRECT_MESSAGE_THREADS_QUERY_KEY]},
    (current) => {
      if (!current?.data) {
        return current;
      }
      return {
        ...current,
        data: current.data.map((thread) => patchThreadAsReadInList(thread, threadId)),
      };
    },
  );

  queryClient.setQueriesData<InfiniteData<GetListResponse<DirectMessageThread>>>(
    {queryKey: [INFINITE_THREADS_QUERY_KEY]},
    (current) => {
      if (!current?.pages) {
        return current;
      }
      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          data: (page.data ?? []).map((thread) => patchThreadAsReadInList(thread, threadId)),
        })),
      };
    },
  );

  queryClient.setQueryData<DirectMessageThreadDetail>(
    [DIRECT_MESSAGE_THREAD_QUERY_KEY, threadId],
    (current) => {
      if (!current) {
        return current;
      }
      return {...current, unread_manager_posts: false};
    },
  );
}

export function dedupePostsById(posts: DirectMessagePost[]): DirectMessagePost[] {
  const seen = new Set<number>();
  return posts.filter((post) => {
    if (seen.has(post.direct_message_post_id)) {
      return false;
    }
    seen.add(post.direct_message_post_id);
    return true;
  });
}

export function appendPostToThreadCache(
  queryClient: QueryClient,
  threadId: number,
  post: DirectMessagePost,
) {
  queryClient.setQueryData<DirectMessageThreadDetail>(
    [DIRECT_MESSAGE_THREAD_QUERY_KEY, threadId],
    (current) => {
      if (!current) {
        return current;
      }
      const posts = dedupePostsById([...current.posts, post]);
      return {...current, posts};
    },
  );
}

export function refetchDirectMessageThread(queryClient: QueryClient, threadId: number) {
  void queryClient.refetchQueries({
    queryKey: [DIRECT_MESSAGE_THREAD_QUERY_KEY, threadId],
    type: "active",
  });
}

export function refetchDirectMessageThreadList(
  queryClient: QueryClient,
  listOptions: DirectMessageListOptions,
) {
  void queryClient.refetchQueries({
    queryKey: [DIRECT_MESSAGE_THREADS_QUERY_KEY],
    predicate: (query) => {
      const params = query.queryKey[1] as DirectMessageListOptions | undefined;
      return (
        params?.mergeSent === listOptions.mergeSent && params?.unread === listOptions.unread
      );
    },
    type: "active",
  });

  void queryClient.refetchQueries({
    queryKey: [INFINITE_THREADS_QUERY_KEY, listOptions],
    type: "active",
  });
}

export function refetchAllDirectMessageThreadLists(queryClient: QueryClient) {
  void queryClient.refetchQueries({
    queryKey: [DIRECT_MESSAGE_THREADS_QUERY_KEY],
    type: "active",
  });
  void queryClient.refetchQueries({
    queryKey: [INFINITE_THREADS_QUERY_KEY],
    type: "active",
  });
}

/** Mark list caches stale so active queries refetch and inactive ones refresh on next mount. */
export function invalidateAllDirectMessageThreadLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({queryKey: [DIRECT_MESSAGE_THREADS_QUERY_KEY]});
  void queryClient.invalidateQueries({queryKey: [INFINITE_THREADS_QUERY_KEY]});
}

export function invalidateNotificationFeed(queryClient: QueryClient) {
  void queryClient.invalidateQueries({queryKey: [NOTIFICATION_FEED_QUERY_KEY]});
}
